import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, FileText, FolderKanban, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CLIENTS } from "@/constants/testIds/crm";
import { useAuth } from "@/context/AuthContext";
import HighlightText from "@/components/data-management/HighlightText";
import SavedViewsBar from "@/components/data-management/SavedViewsBar";
import StatusCountCards from "@/components/data-management/StatusCountCards";
import PaginationBar from "@/components/data-management/PaginationBar";
import { normalizeListResponse } from "@/lib/listResponse";
import { getStatusLabel, getStatusToneClasses } from "@/lib/statusStyles";

const SORT_OPTIONS = [
    { value: "-created_at", label: "Newest first" },
    { value: "created_at", label: "Oldest first" },
    { value: "company", label: "Company A-Z" },
    { value: "-company", label: "Company Z-A" },
    { value: "client_name", label: "Client A-Z" },
    { value: "source", label: "Source A-Z" },
];

export default function Clients() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [detail, setDetail] = useState(null);
    const [q, setQ] = useState("");
    const [source, setSource] = useState("");
    const [sort, setSort] = useState("-created_at");

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            params.set("page", page);
            params.set("limit", 12);
            if (q) params.set("q", q);
            if (source) params.set("source", source);
            if (sort) params.set("sort", sort);
            const { data } = await api.get(`/clients?${params.toString()}`);
            const normalized = normalizeListResponse(data);
            setItems(normalized.items);
            setCounts(normalized.counts);
            setTotal(normalized.total);
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Failed to load clients");
            setItems([]);
            setCounts({});
            setTotal(0);
        }
    }, [page, q, source, sort]);
    useEffect(() => { load(); }, [load]);

    const sources = useMemo(() => Array.from(new Set(items.map((c) => c.source).filter(Boolean))).sort(), [items]);
    const viewState = { q, source, sort };
    const applyState = ({ q: nextQ = "", source: nextSource = "", sort: nextSort = "-created_at" }) => {
        setQ(nextQ);
        setSource(nextSource);
        setSort(nextSort);
        setPage(1);
    };
    const sourceCards = Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 4)
        .map(([label, value]) => ({ label, value, hint: "Clients by source" }));

    return (
        <div className="space-y-6" data-testid={CLIENTS.root}>
            <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary">Delivery</div>
                <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">Clients</h1>
                <p className="text-sm text-muted-foreground mt-2">Won leads automatically roll up here with their linked projects and invoices.</p>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <SavedViewsBar
                    storageKey="crm.clients.views"
                    state={viewState}
                    applyState={applyState}
                    defaultState={{ q: "", source: "", sort: "-created_at" }}
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                    value={q}
                    onChange={(e) => { setPage(1); setQ(e.target.value); }}
                    placeholder="Search company, client, email, phone…"
                    className="md:col-span-1 h-10 px-3 rounded-md border border-input bg-card text-sm"
                />
                <select value={source} onChange={(e) => { setPage(1); setSource(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    <option value="">All sources</option>
                    {sources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} className="h-10 px-3 rounded-md border border-input bg-card text-sm">
                    {SORT_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
            </div>

            <StatusCountCards
                cards={[
                    { label: "Total Clients", value: total, hint: "All matching records" },
                    ...sourceCards,
                ]}
                className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.length === 0 && <div className="text-sm text-muted-foreground col-span-full text-center py-12">No clients yet.</div>}
                {items.map((c) => (
                    <button key={c.id} onClick={() => setDetail(c)} className={`text-left border rounded-md p-5 hover:-translate-y-[1px] transition-colors transition-transform ${getStatusToneClasses(c.source, { group: "source", variant: "surface" })}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="font-heading text-lg font-semibold tracking-tight"><HighlightText text={c.company} query={q} /></div>
                                <div className="text-sm text-muted-foreground mt-1"><HighlightText text={c.client_name} query={q} /></div>
                            </div>
                            <FolderKanban className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="mt-4 text-xs text-muted-foreground space-y-1">
                            <div><HighlightText text={c.email || c.client_email || "—"} query={q} /></div>
                            <div><HighlightText text={c.phone || "—"} query={q} /></div>
                            <div><HighlightText text={c.website || "—"} query={q} /></div>
                            <div className="pt-1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(c.source, { group: "source", variant: "badge" })}`}>
                                    {getStatusLabel(c.source)}
                                </span>
                            </div>
                        </div>
                    </button>
                ))}
            </div>

            <PaginationBar total={total} page={page} limit={12} onPageChange={setPage} />

            {detail && <ClientDrawer client={detail} onClose={() => setDetail(null)} />}
        </div>
    );
}

function ClientDrawer({ client, onClose }) {
    const { isAdmin } = useAuth();
    const [data, setData] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get(`/clients/${client.id}`)
            .then((r) => { if (!cancelled) setData(r.data); })
            .catch((err) => toast.error(err.response?.data?.detail || err.message || "Failed to load client"));
        return () => { cancelled = true; };
    }, [client.id]);

    const remove = async () => {
        if (!window.confirm(`Delete client "${client.company}"?`)) return;
        await api.delete(`/clients/${client.id}`);
        toast.success("Client deleted");
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-background/70 backdrop-blur-sm" onClick={onClose}>
            <aside onClick={(e) => e.stopPropagation()} className="ml-auto h-full w-full max-w-3xl bg-card border-l border-border overflow-y-auto">
                <div className="p-6 border-b border-border sticky top-0 bg-card z-10">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground">Client</div>
                            <h3 className="font-heading text-2xl font-bold tracking-tight mt-1">{client.company}</h3>
                            <div className="text-sm text-muted-foreground">{client.client_name}</div>
                        </div>
                        <div className="flex items-center gap-2">
                            {isAdmin && (
                                <button onClick={remove} className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-destructive/40 text-destructive text-xs hover:bg-destructive/10">
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                </button>
                            )}
                            <button onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-secondary"><X className="h-4 w-4" /></button>
                        </div>
                    </div>
                </div>

                <div className="p-6 space-y-6">
                    <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                            ["Company Email", data?.company_email || client.company_email || "—"],
                            ["Email", data?.client_email || client.client_email || client.email || "—"],
                            ["Phone", data?.phone || client.phone || "—"],
                            ["Website", data?.website || client.website || "—"],
                            ["Address", [data?.city || client.city, data?.state || client.state, data?.country || client.country].filter(Boolean).join(", ") || "—"],
                            ["Source", data?.source || client.source || "—"],
                        ].map(([label, value]) => (
                            <div key={label} className="border border-border rounded-md p-4">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
                                <div className="mt-1 text-sm break-words">{value}</div>
                            </div>
                        ))}
                    </section>

                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Associated Projects</h4>
                        <div className="space-y-2">
                            {(data?.projects || []).map((p) => (
                                <div key={p.id} className="flex items-center justify-between gap-3 border border-border rounded-md p-3">
                                    <div>
                                        <div className="text-sm font-medium">{p.name}</div>
                                <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(p.status, { group: "status", variant: "badge" })}`}>{getStatusLabel(p.status)}</div>
                            </div>
                                    {p.deadline && <div className="text-xs font-mono text-muted-foreground inline-flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {new Date(p.deadline).toLocaleDateString()}</div>}
                                </div>
                            ))}
                            {(data?.projects || []).length === 0 && <div className="text-sm text-muted-foreground">No projects linked yet.</div>}
                        </div>
                    </section>

                    <section>
                        <h4 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-3">Associated Invoices</h4>
                        <div className="space-y-2">
                            {(data?.invoices || []).map((inv) => (
                                <div key={inv.id} className="flex items-center justify-between gap-3 border border-border rounded-md p-3">
                                    <div>
                                        <div className="text-sm font-medium">{inv.invoice_number}</div>
                                        <div className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${getStatusToneClasses(inv.status, { group: "payment", variant: "badge" })}`}>{getStatusLabel(inv.status)}</div>
                                    </div>
                                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                                        <FileText className="h-3.5 w-3.5" /> ${(inv.total || 0).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                            {(data?.invoices || []).length === 0 && <div className="text-sm text-muted-foreground">No invoices yet.</div>}
                        </div>
                    </section>
                </div>
            </aside>
        </div>
    );
}
